import { formatLongDate } from '@/lib/format-long-date';
import { Button, Flex, Text } from '@chakra-ui/react';
import { Alert } from '@fluenceur/ui';
import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

/** Where the migration card lives, and the only place the approval starts. */
const BILLING_PATH = '/settings/billing';

export interface ShopifyApprovalNoticeProps {
  /** ISO instant the dashboard closes if the replacement is never approved. */
  deadline: string;
}

/**
 * The second of the two steps, and the one nothing was carrying.
 *
 * Accepting the terms settles the first step and nothing else: the lock closes
 * the dashboard of any organization that has not approved the replacement in
 * Shopify, whether or not it accepted. The notice used to unmount on
 * acceptance, so the merchants who did exactly what was asked of them were the
 * ones walking into the lock with nothing on screen to warn them.
 *
 * It points at the billing page rather than calling Shopify itself. One place
 * owns that call, with a wording per state and its own error handling; a notice
 * that started attempts of its own would be a second owner of the same money,
 * and the two would drift.
 */
export const ShopifyApprovalNotice: React.FC<ShopifyApprovalNoticeProps> = ({ deadline }) => {
  const { t } = useTranslation();

  return (
    <Alert
      size="sm"
      status="warning"
      py={2}
      borderRadius="0"
      justifyContent="center"
      fontSize="13px"
    >
      <Flex align="center" gap={2} gapX={4} justifyContent="center" wrap="wrap">
        <Text>
          {t('legal.successFeeTerms.banner.approvalMessage', {
            date: formatLongDate(deadline, t('common.locale')),
          })}
        </Text>
        <Button asChild variant="solid" size="xs">
          <RouterLink to={BILLING_PATH}>
            {t('legal.successFeeTerms.banner.approvalAction')}
          </RouterLink>
        </Button>
      </Flex>
    </Alert>
  );
};
